import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalButton } from '@ovasabi/ui-minimal'
import { CloseIcon } from '../icons'

/**
 * ChowModal — the project's reusable overlay primitive.
 *
 * A content modal that renders as a bottom sheet on phones (where it belongs on a
 * mobile shell) and a centered dialog on wider viewports. Unlike the foundation
 * `MinimalActionModal` (a confirm/cancel dialog with a fixed tone kicker + footer),
 * this hosts arbitrary content with no forced chrome — for pickers, filters, and
 * detail sheets. Closes on backdrop click and Escape, and locks body scroll while
 * open. Reuse it anywhere a content overlay is needed.
 *
 * Entrance uses CSS keyframes (not JS animation), so the backdrop always fully
 * dims and the sheet is always fully opaque — it can never get stuck mid-tween on
 * a busy render loop or a throttled frame clock.
 */
const Style = {
  Backdrop: styled.div`
    position: fixed;
    inset: 0;
    z-index: ${minimalVars.zIndex.overlay};
    background: ${minimalVars.color.bgOverlay};
    display: flex;
    align-items: flex-end;
    justify-content: center;
    animation: fadeIn 160ms ease forwards;

    /* 560px is between the hand and page stops, so it stays hand-typed. */
    @media (min-width: 560px) {
      align-items: center;
      padding: ${minimalVars.space.sm};
    }
  

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`,
  Sheet: styled.div<{ $maxWidth?: number | string }>`
    position: relative;
    z-index: ${minimalVars.zIndex.modal};
    width: 100%;
    max-width: ${({ $maxWidth }) =>
      typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth || '520px'};
    max-height: 86dvh;
    display: flex;
    flex-direction: column;
    background: ${minimalVars.color.bgSurface};
    border-top-left-radius: ${minimalVars.radius.xl};
    border-top-right-radius: ${minimalVars.radius.xl};
    box-shadow: 0 -10px 44px rgba(0, 0, 0, 0.2);
    will-change: transform, opacity;
    transform: translateZ(0);
    animation: slideUp 220ms cubic-bezier(0.22, 1, 0.36, 1) forwards;

    /* 560px is between the hand and page stops, so it stays hand-typed. */
    @media (min-width: 560px) {
      border-radius: ${minimalVars.radius.xl};
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
    }
  

  @keyframes slideUp {
    from { transform: translateY(24px) translateZ(0); opacity: 0.4; }
    to { transform: translateY(0) translateZ(0); opacity: 1; }
  }
`,
  Grip: styled.span`
    width: 40px;
    height: 4px;
    border-radius: ${minimalVars.radius.pill};
    background: ${minimalVars.color.borderStrong};
    margin: 8px auto 0;

    /* 560px is between the hand and page stops, so it stays hand-typed. */
    @media (min-width: 560px) {
      display: none;
    }
  `,
  Header: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    padding: ${minimalVars.space.xs} ${minimalVars.space.sm};
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
  `,
  Title: styled.h2`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    font-size: ${minimalVars.typography.h2Size};
    line-height: 1.35;
    color: ${minimalVars.color.textPrimary};
  `,
  Close: styled(MinimalButton)`
    display: grid;
    place-items: center;
    width: 44px;
    min-width: 44px;
    height: 44px;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    cursor: pointer;
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.brandSoft};
    color: ${minimalVars.color.brand};
    transition: filter 140ms ease;

    &:hover {
      filter: brightness(0.97);
    }
    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 2px;
    }
  `,
  Body: styled.div`
    padding: ${minimalVars.space.sm};
    min-width: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `,
}

export interface ChowModalProps {
  open: boolean
  title?: ReactNode
  maxWidth?: number | string
  hideHeader?: boolean
  ariaLabel?: string
  onClose: () => void
  children: ReactNode
}

export const ChowModal = ({
  open,
  title,
  maxWidth,
  hideHeader = false,
  ariaLabel,
  onClose,
  children,
}: ChowModalProps) => {
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <Style.Backdrop onClick={onClose} role="presentation">
      <Style.Sheet
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : 'Modal Dialog')}
        $maxWidth={maxWidth}
        onClick={(event) => event.stopPropagation()}
      >
        <Style.Grip aria-hidden />
        {!hideHeader && (
          <Style.Header>
            <Style.Title>{title}</Style.Title>
            <Style.Close variant="quiet" type="button" aria-label="Close" onClick={onClose}>
              <CloseIcon size="sm" />
            </Style.Close>
          </Style.Header>
        )}
        <Style.Body>{children}</Style.Body>
      </Style.Sheet>
    </Style.Backdrop>,
    document.body,
  )
}
